import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useWebClientApi } from "src/api";
import { UserContext, useAuth } from "src/context";
import { logSentryError } from "src/utils";
import {
  getBorrowerOwnershipErrorMessage,
  useApiErrorHandler,
} from "./useApiErrorHandler";

export default () => {
  const { userData } = useContext(UserContext);
  const { dealerName } = useParams();
  const { user: firebaseUser } = useAuth();
  const api = useWebClientApi();
  const navigate = useNavigate();
  const { handleApiError } = useApiErrorHandler();
  const [errorMessage, setErrorMessage] = useState("");
  const [docusignLoading, setDocusignLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const retries = searchParams.get("retries") ?? "0";
  let retryCount = parseInt(retries, 10);
  if (isNaN(retryCount)) {
    retryCount = 0;
  }

  const docusignStuff = async () => {
    if (!userData || !firebaseUser) return;

    let envelope_id: string | undefined;
    let envelopeRetryCount = 3;

    while (envelopeRetryCount > 0 && typeof envelope_id === "undefined") {
      console.log(`Envelope ID attempt #${envelopeRetryCount}`);
      envelopeRetryCount--;
      const envelopeData = await api.createEnvelope();
      envelope_id = envelopeData.envelope_id;
    }

    setDocusignLoading(true);

    if (!envelope_id || envelope_id.length === 0) {
      alert("Error: No envelope found");
      navigate(-1);
      return;
    }

    console.log("ENVELOPE: ", envelope_id);
    let signData: Awaited<ReturnType<typeof api.signApplication>>;
    try {
      signData = await api.signApplication({
        envelope_id,
        return_url: `${__FRONTEND_BASE_URL__}/${dealerName}/application/${userData.application_id}/progress?signer_id=${userData.borrower_id}`,
      });
    } catch (error) {
      // Signing for the other borrower's account is rejected (DEA-903)
      if (getBorrowerOwnershipErrorMessage(error)) {
        handleApiError(error as Error);
        navigate(-1);
        return;
      }
      throw error;
    }
    const signing_url = signData.signing_url;

    if (!signing_url || signing_url.length === 0) {
      alert("Error: No signing url found");
      navigate(-1);
      return;
    }
    /**
     * Custom Sentry error logger for Docusign load times that take longer than 1 minute.
     */
    const docusignSentryThresholdTime = 1000 * 60;
    const docusignSentryThresholdTimer = setTimeout(() => {
      if (retryCount === 0) {
        logSentryError(
          new Error(
            `Docusign load time over 1 minute threshold. Envelope: ${envelope_id}, AppId: ${userData?.application_id}, Dealer: ${dealerName}.`,
          ),
          "signature",
          "docusign-load-time-threshold",
        );
      }
    }, docusignSentryThresholdTime);

    /**
     * Give docusign a full 2 minutes to load, then refresh the page if it hasn't,
     * because sometimes it just stalls.
     * Increase wait time by a minute each retry, and after 3 tries, just display an error message.
     */
    const docusignReloadTime = (120 + retryCount * 60) * 1000;
    const docusignReloadTimer = setTimeout(() => {
      if (retryCount < 2) {
        retryCount++;
        const url = new URL(window.location.href);
        url.searchParams.set("retries", retryCount.toString());
        window.location.href = url.toString();
        return;
      }

      setErrorMessage(
        "Unable to load form for signature. Please contact a dealer representative if this issue persists.",
      );
      logSentryError(
        new Error("Max retries exceeded: Docusign unable to load."),
        "signature",
        "max-retries-exceeded",
      );
    }, docusignReloadTime);
    const docusignIntegrationKeyId = __DOCUSIGN_INTEGRATION_KEY_ID__;
    const docusign = await window.DocuSign.loadDocuSign(
      docusignIntegrationKeyId,
    );
    const signing = docusign.signing({
      url: signing_url,
      displayFormat: "focused",
      style: {
        branding: {
          primaryButton: {
            color: "white",
            backgroundColor: "rgba(49, 57, 74, 1)",
          },
        },
        signingNavigationButton: {
          position: "bottom-right",
        },
      },
    });

    signing.on("sessionEnd", (evt: { returnUrl: string }) => {
      console.log("DOCUSIGN SESSION END:: ", evt);
      navigate(evt.returnUrl.replace(__FRONTEND_BASE_URL__, ""));
    });
    signing.on("ready", (evt: unknown) => {
      console.log("DOCUSIGN READY", evt);
      setDocusignLoading(false);
      clearTimeout(docusignReloadTimer);
      clearTimeout(docusignSentryThresholdTimer);
    });
    signing.mount("#docusign");
    window.dispatchEvent(new Event("resize"));
  };

  useEffect(() => {
    void docusignStuff();
  }, []);

  return { errorMessage, docusignLoading };
};
